import { openModal } from "~/common/utils/ModalService";
import { type AddToCartFormData, type AddToCartHookProps } from "~/features/addToCart/hooks/useAddToCartNew";
import {
  type AddToCartSubmitHandler,
  AddToCartWizard,
} from "~/features/addToCart/components/AddToCartProcess/AddToCartWizard";
import { addToCartApi } from "~/features/addToCart/redux/addToCart/actions";
import { EditPartWizard } from "~/features/addToCart/components/AddToCartProcess/EditPartWizard";
import { Permission, userHasAllPermissions } from "~/domains/user/utils/permissions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { AddToCartSource } from "~/app/analytics/types";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";

async function openAddToCartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode"> & {
    restrictionQuery: string;
    source?: AddToCartSource;
    showSuccessDrawer?: boolean;
    position?: number;
    onSuccessCallback?: () => void;
    onCancelCallback?: () => void;
  },
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
) {
  const addToCartSource: AddToCartSource = hookProps.source ?? "unknown";
  const onSubmit = (descriptor: ProductDescriptor, formData: AddToCartFormData) => {
    return addToCartApi({
      productCode: descriptor.base.code,
      restrictionQuery: hookProps.restrictionQuery,
      ...formData,
      addToCartSource,
      context,
      position: hookProps.position,
      showSuccessDrawer: hookProps.showSuccessDrawer,
      onSuccessCallback: hookProps.onSuccessCallback,
    });
  };

  if (userHasAllPermissions(Permission.AVAILABILITY, Permission.PRICE)) {
    // Customer flow

    return openModal("add-to-cart-modal", (close) => (
      <AddToCartWizard
        onSubmit={onSubmit}
        editMode={false}
        closePopup={(openPreviousModal?: boolean) => {
          close();
          if (openPreviousModal && hookProps.onCancelCallback) {
            hookProps.onCancelCallback();
          }
        }}
        {...hookProps}
      />
    ));
  }

  // Non customer flow. Here we don't know availabilities or prices, so we just perform an add to cart call with all possible data faked

  return addToCartApi({
    productCode: hookProps.productCode,
    restrictionQuery: hookProps.restrictionQuery,
    netPrice: null,
    priceUnit: 1,
    priceReference: getInitialPriceReference(),
    quantity: hookProps.initialData?.quantity ?? 1,
    selectedPackCode: INVALID_PACKAGE_CODE,
    selectedPack: null,
    surcharge: 0,
    totalQuantity: 1, // TODO check if this is fine?
    optionalParts: {},
    salePriceEnabled: false,
    addToCartSource,
    context,
  });
}

async function openEditPartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode"> & { restrictionQuery: string; onSubmit: AddToCartSubmitHandler },
) {
  return openModal("edit-cart-modal", (close) => {
    return <EditPartWizard editMode={true} closePopup={close} {...hookProps} />;
  });
}

export const AddToCartService = {
  openAddToCartWizard,
  openEditPartWizard,
};
